/**
 * @fileoverview Measurement widget — number + unit suffix pill.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render NumberField behavior plus a unit pill from `extras.unit`. Unit is
 *   template metadata only; not persisted (per D-0.13 schema audit notes).
 *
 * @component MeasurementField
 */
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';

export function MeasurementField(props: BaseWidgetProps<number | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as { min?: number; max?: number; step?: number; unit?: string };
  const aboveMax = extras.max != null && value != null && value > extras.max;
  const belowMin = extras.min != null && value != null && value < extras.min;
  const computedError = errorMessage
    ?? (aboveMax ? `Max ${extras.max}` : undefined)
    ?? (belowMin ? `Min ${extras.min}` : undefined);

  function commit(text: string) {
    if (text === '') return onChange(null);
    const n = Number(text);
    if (Number.isNaN(n)) return;
    if (extras.min != null && n < extras.min) return;
    if (extras.max != null && n > extras.max) return;
    onChange(n);
  }

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="measurement"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={computedError}
      requiredUnmet={requiredUnmet} requiredImmediate={props.requiredImmediate}
    >
      <div className="flex items-center gap-2">
        <input
          id={field.id}
          type="number"
          value={value ?? ''}
          onChange={(e) => commit(e.target.value)}
          min={extras.min}
          max={extras.max}
          step={extras.step}
          disabled={!!resolved?.disabled}
          aria-required={!!(field.required || resolved?.required)}
          aria-invalid={!!computedError}
          className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-sm outline-hidden focus:ring-2 focus:ring-orange-300 disabled:opacity-50"
        />
        {extras.unit && (
          <span data-unit={extras.unit} className="inline-flex items-center px-2 py-1 text-xs font-medium bg-slate-100 rounded-sm text-slate-700">
            {extras.unit}
          </span>
        )}
      </div>
    </FieldWrapper>
  );
}
